import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Diamond, Folder, Layers, Plus, X } from 'lucide-react';
import type { AssigneeDoc, WbsItemDoc } from '@mct/shared';
import { cn } from '../../lib/utils';
import {
  buildDependencyCandidates,
  filterCandidates,
  statusDotClass,
  type WbsDepCandidate,
} from '../../lib/wbsDependencyPicker';

/**
 * Asana / Linear style dependency editor.
 *
 * - Each entry in `dependsOn` is rendered as an interactive chip with a
 *   close button that immediately commits the removal.
 * - Click a chip body → bubble up `onSelectItem(id)` so the parent page can
 *   focus that row in the tree (and scroll it into view).
 * - "+ 追加" opens a popover with a search input and a filtered list of
 *   eligible WBS items. Eligibility (excluding self / ancestors / descendants
 *   / current deps / cycle-creating picks) lives in `wbsDependencyPicker`.
 *
 * Keyboard model in the popover:
 *   - ↑/↓  navigates `activeIndex`.
 *   - Enter commits the active candidate.
 *   - Esc closes without committing.
 *   - Tab / clicking outside closes via the document `mousedown` listener.
 */
export function DependencyChips({
  item,
  allItems,
  assignees = [],
  onChange,
  onSelectItem,
}: {
  item: WbsItemDoc;
  allItems: readonly WbsItemDoc[];
  /** Optional — used to render the assignee label on each candidate row. */
  assignees?: readonly AssigneeDoc[];
  onChange: (next: string[]) => void | Promise<void>;
  onSelectItem?: (itemId: string) => void;
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const addBtnRef = useRef<HTMLButtonElement | null>(null);

  const dependsOn = item.dependsOn;

  const itemsById = useMemo(
    () => new Map(allItems.map((i) => [i.itemId, i] as const)),
    [allItems],
  );

  // Resolve current dependency ids to display rows. Drop dangling refs
  // silently — the cleanup happens elsewhere; here we just don't render
  // ghost chips.
  const chips = useMemo(
    () =>
      dependsOn
        .map((id) => itemsById.get(id))
        .filter((x): x is WbsItemDoc => Boolean(x)),
    [dependsOn, itemsById],
  );

  const remove = (id: string) => {
    void onChange(dependsOn.filter((d) => d !== id));
  };

  const add = (id: string) => {
    if (dependsOn.includes(id)) return;
    void onChange([...dependsOn, id]);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1">
        {chips.length === 0 ? (
          <span className="text-[11px] text-slate-400">
            先行タスクなし
          </span>
        ) : (
          chips.map((c) => (
            <DependencyChip
              key={c.itemId}
              item={c}
              onRemove={() => remove(c.itemId)}
              onClick={() => onSelectItem?.(c.itemId)}
            />
          ))
        )}
        <button
          ref={addBtnRef}
          type="button"
          onClick={() => setPopoverOpen((v) => !v)}
          className={cn(
            'inline-flex items-center gap-0.5 rounded-md border border-dashed border-slate-300 px-1.5 py-0.5 text-[11px] text-slate-600 hover:border-slate-400 hover:bg-slate-50',
            popoverOpen ? 'border-sky-400 bg-sky-50 text-sky-700' : '',
          )}
          aria-haspopup="listbox"
          aria-expanded={popoverOpen}
          aria-label="先行タスクを追加"
        >
          <Plus className="h-3 w-3" />
          追加
        </button>
      </div>
      {popoverOpen ? (
        <DependencyPickerPopover
          item={item}
          allItems={allItems}
          assignees={assignees}
          currentDeps={dependsOn}
          anchor={addBtnRef.current}
          onClose={() => setPopoverOpen(false)}
          onPick={(id) => {
            add(id);
            setPopoverOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

function KindIcon({ kind }: { kind: WbsItemDoc['kind'] }) {
  if (kind === 'phase') {
    return <Folder className="h-3 w-3 shrink-0 text-blue-600" aria-hidden />;
  }
  if (kind === 'milestone') {
    return <Diamond className="h-3 w-3 shrink-0 text-amber-600" aria-hidden />;
  }
  return <Layers className="h-3 w-3 shrink-0 text-slate-500" aria-hidden />;
}

function DependencyChip({
  item,
  onRemove,
  onClick,
}: {
  item: WbsItemDoc;
  onRemove: () => void;
  onClick: () => void;
}) {
  return (
    <span
      className="group inline-flex max-w-[220px] items-center gap-1 rounded-full border border-slate-200 bg-slate-50 py-0.5 pl-1.5 pr-0.5 text-[11px] text-slate-700 shadow-sm hover:border-slate-300 hover:bg-white"
      data-dep-chip={item.itemId}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex min-w-0 items-center gap-1 outline-none focus-visible:ring-1 focus-visible:ring-sky-400 rounded-full"
        title={item.name}
      >
        <KindIcon kind={item.kind} />
        <span
          className={cn(
            'inline-block h-1.5 w-1.5 shrink-0 rounded-full',
            statusDotClass(item.status),
          )}
          aria-hidden
        />
        {/*
         * sortKey badge intentionally absent — the prefix ("1-5-7" /
         * "3-7") was visual noise that confused users in the picker
         * popover and chips. Same reasoning as PR #23 dropping the
         * sortKey badge from the detail-panel header.
         */}
        <span className="truncate">{item.name}</span>
      </button>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`${item.name} を依存から外す`}
        className="ml-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-red-600"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

interface PopoverPlacement {
  top: number;
  left: number;
  width: number;
}

function DependencyPickerPopover({
  item,
  allItems,
  assignees,
  currentDeps,
  anchor,
  onClose,
  onPick,
}: {
  item: WbsItemDoc;
  allItems: readonly WbsItemDoc[];
  assignees: readonly AssigneeDoc[];
  currentDeps: readonly string[];
  anchor: HTMLElement | null;
  onClose: () => void;
  onPick: (id: string) => void;
}) {
  const [rawQuery, setRawQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [placement, setPlacement] = useState<PopoverPlacement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  // Debounce: keeps the candidate list calm while the user types fast.
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(rawQuery), 120);
    return () => window.clearTimeout(t);
  }, [rawQuery]);

  const baseCandidates = useMemo(
    () => buildDependencyCandidates(item, allItems, currentDeps, assignees),
    [item, allItems, currentDeps, assignees],
  );

  const visible = useMemo(
    () => filterCandidates(baseCandidates, debouncedQuery),
    [baseCandidates, debouncedQuery],
  );

  // When the candidate list shifts (typing a new query), keep the
  // highlighted index in range so Enter doesn't pick a stale row.
  useEffect(() => {
    setActiveIndex((prev) => {
      if (visible.length === 0) return 0;
      return Math.min(prev, visible.length - 1);
    });
  }, [visible]);

  useLayoutEffect(() => {
    if (!anchor) return;
    const update = () => {
      const r = anchor.getBoundingClientRect();
      setPlacement({
        top: r.bottom + 4,
        left: r.left,
        width: 320,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchor]);

  // Close on outside mousedown / Escape. Mousedown (not click) so the
  // popover dismisses before any conflicting downstream click handler.
  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (popoverRef.current?.contains(target)) return;
      if (anchor?.contains(target ?? null)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onClose]);

  // Keep the active row visible inside the scroll container.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLLIElement>(
      `[data-cand-index="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!placement) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) =>
        visible.length === 0 ? 0 : (i + 1) % visible.length,
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) =>
        visible.length === 0 ? 0 : (i - 1 + visible.length) % visible.length,
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = visible[activeIndex];
      if (pick) onPick(pick.itemId);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const popover = (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="先行タスクを検索して追加"
      className="z-50 overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg"
      style={{
        position: 'fixed',
        top: placement.top,
        left: placement.left,
        width: placement.width,
      }}
      onKeyDown={onKeyDown}
    >
      <div className="border-b border-slate-200 p-2">
        <input
          autoFocus
          type="text"
          placeholder="タスクを検索 (キー / 名称 / 担当)"
          value={rawQuery}
          onChange={(e) => setRawQuery(e.target.value)}
          className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-200"
          aria-controls="dep-picker-list"
          aria-activedescendant={
            visible[activeIndex]
              ? `dep-cand-${visible[activeIndex]!.itemId}`
              : undefined
          }
        />
      </div>
      <ul
        ref={listRef}
        id="dep-picker-list"
        role="listbox"
        className="max-h-64 overflow-auto py-1"
      >
        {visible.length === 0 ? (
          <li className="px-3 py-4 text-center text-xs text-slate-500">
            該当するタスクがありません
          </li>
        ) : (
          visible.map((c, idx) => (
            <DependencyCandidateRow
              key={c.itemId}
              candidate={c}
              active={idx === activeIndex}
              index={idx}
              onHover={() => setActiveIndex(idx)}
              onPick={() => onPick(c.itemId)}
            />
          ))
        )}
      </ul>
    </div>
  );

  return createPortal(popover, document.body);
}

function DependencyCandidateRow({
  candidate,
  active,
  index,
  onHover,
  onPick,
}: {
  candidate: WbsDepCandidate;
  active: boolean;
  index: number;
  onHover: () => void;
  onPick: () => void;
}) {
  return (
    <li
      id={`dep-cand-${candidate.itemId}`}
      role="option"
      aria-selected={active}
      data-cand-index={index}
      onMouseMove={onHover}
      onMouseDown={(e) => {
        // mousedown so the click isn't lost when blur fires first.
        e.preventDefault();
        onPick();
      }}
      className={cn(
        'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs',
        active ? 'bg-sky-100 text-sky-900' : 'text-slate-700 hover:bg-slate-50',
      )}
    >
      <KindIcon kind={candidate.kind} />
      <span
        className={cn(
          'inline-block h-1.5 w-1.5 shrink-0 rounded-full',
          statusDotClass(candidate.status),
        )}
        aria-hidden
      />
      {/*
       * sortKey prefix intentionally not rendered (see DependencyChip).
       * Title-only rows match the panel header in PR #23.
       */}
      <span className="min-w-0 flex-1 truncate">{candidate.name}</span>
      {candidate.assigneeName ? (
        <span className="truncate text-[10px] text-slate-400">
          {candidate.assigneeName}
        </span>
      ) : null}
    </li>
  );
}
